<template>
  <section class="matches-page">

    <!-- ================= HEADER ================= -->
    <header class="page-header">

      <div class="header-info">

        <div class="title-icon">
          ⚽
        </div>

        <div>
          <div class="breadcrumbs">
            خانه
            <span>/</span>
            مسابقات
          </div>

          <h1>مسابقات</h1>

          <p>
            نتایج و برنامه بازی‌های فوتبال از همه لیگ‌ها
          </p>
        </div>

      </div>

      <button class="league-filter">
        <span>⚱</span>
        فیلتر لیگ‌ها
      </button>

    </header>


    <!-- ================= DATE NAVIGATION ================= -->
    <section class="date-navigation">

      <!-- روز قبل -->
      <button
          class="date-side"
          @click="changeDay(-1)"
      >

        <span class="arrow">‹</span>

        <div>
          <span>
            {{ previousDay.weekday }}
          </span>

          <small>
            {{ previousDay.date }}
          </small>
        </div>

      </button>


      <!-- امروز -->
      <button
          class="today-date"
          @click="goToday"
      >

        <strong>
          {{ currentDay.weekday }}
        </strong>

        <span class="today-full-date">
          {{ currentDay.date }}
        </span>

      </button>


      <!-- روز بعد -->
      <button
          class="date-side"
          @click="changeDay(1)"
      >

        <div>
          <span>
            {{ nextDay.weekday }}
          </span>

          <small>
            {{ nextDay.date }}
          </small>
        </div>

        <span class="arrow">›</span>

      </button>

    </section>

    <!-- ================= LOADING ================= -->
    <div
        v-if="loading"
        class="loading"
    >
      <div class="loader"></div>
      <span>در حال دریافت بازی‌ها...</span>
    </div>


    <!-- ================= ERROR ================= -->
    <div
        v-else-if="error"
        class="error-box"
    >
      <span>⚠️</span>
      {{ error }}

      <button @click="loadMatches">
        تلاش مجدد
      </button>
    </div>


    <!-- ================= EMPTY ================= -->
    <div
        v-else-if="!groupedMatches.length"
        class="empty-state"
    >
      <div class="empty-icon">
        ⚽
      </div>

      <h3>
        بازی‌ای برای این روز پیدا نشد
      </h3>

      <p>
        برای مشاهده بازی‌های روزهای دیگر از تقویم بالا استفاده کنید.
      </p>
    </div>


    <!-- ================= LEAGUES ================= -->
    <div
        v-else
        class="competitions"
    >

      <section
          v-for="competition in groupedMatches"
          :key="competition.id"
          class="competition-section"
      >

        <!-- Competition Header -->
        <div class="competition-header">

          <div class="competition-title">

            <div
                v-if="competition.logo"
                class="competition-logo"
            >
              <img
                  :src="competition.logo"
                  :alt="competition.name"
              >
            </div>

            <div
                v-else
                class="competition-placeholder"
            >
              ⚽
            </div>

            <h2>
              {{ getLeagueName(competition.name) }}
            </h2>

          </div>

          <span class="match-count">
            {{ competition.matches.length }} بازی
          </span>

        </div>


        <!-- Matches -->
        <div class="competition-matches">

          <article
              v-for="match in competition.matches"
              :key="match.id"
              class="match-row"
              @click="openMatch(match.id)"
          >

            <!-- Home -->
            <div
                class="team home-team clickable-team"
                @click.stop="$router.push(`/team/${match.home?.team?.slug || match.home?.id}`)"
            >
              <img
                  v-if="match.home?.team?.logo"
                  :src="match.home.team.logo"
                  :alt="match.home.team.name"
              >
              <span>
                  {{ getTeamName(match.home?.team?.name) || 'نامشخص' }}
                </span>

            </div>


            <!-- Center -->
            <!-- Center -->
            <div class="match-center">

              <!-- Finished -->
              <template v-if="match.status === 'FINISHED'">

                <strong class="score finished-score">
                  {{ match.home?.score ?? 0 }}
                  -
                  {{ match.away?.score ?? 0 }}
                </strong>

                <span class="status-dot finished-dot">
      پایان
    </span>

              </template>


              <!-- Live -->
              <template v-else-if="isLive(match.status)">

                <div class="live-center">

                  <strong class="score live-score">
                    {{ match.home?.score ?? 0 }}
                    -
                    {{ match.away?.score ?? 0 }}
                  </strong>

                  <span class="status-dot live-dot">
        <i></i>
        زنده
      </span>

                </div>

              </template>


              <!-- Scheduled -->
              <template v-else>

                <strong class="kickoff">
                  {{ formatTime(match.kickoffAt) }}
                </strong>

              </template>

            </div>


            <!-- Away -->
            <div
                class="team away-team clickable-team"
                @click.stop="$router.push(`/team/${match.away?.team?.slug || match.away?.id}`)"
            >
               <span>
                  {{ getTeamName(match.away?.team?.name) || 'نامشخص' }}
               </span>
              <img
                  v-if="match.away?.team?.logo"
                  :src="match.away.team.logo"
                  :alt="match.away.team.name"
              >

            </div>
          </article>

        </div>

      </section>

    </div>


    <!-- ================= BOTTOM CTA ================= -->
    <div class="matches-footer">

      <div class="footer-icon">
        📅
      </div>

      <div>

        <strong>
          دنبال بازی خاصی هستید؟
        </strong>

        <p>
          می‌توانید با انتخاب لیگ یا تاریخ مورد نظر،
          برنامه بازی‌ها را پیدا کنید.
        </p>

      </div>

    </div>

  </section>
</template>


<script>

import api from '@/services/api'
import {teamNames} from "@/utils/translations";
import {leagueNamesLatin} from '@/utils/translations';

export default {

  name: 'Matches',

  data() {

    return {

      selectedDate: new Date(),

      matches: [],

      loading: false,

      error: null

    }

  },


  computed: {

    currentDay() {
      return this.formatPersianDate(this.selectedDate)
    },


    previousDay() {

      const date = new Date(this.selectedDate)

      date.setDate(date.getDate() - 1)

      return this.formatPersianDate(date)

    },


    nextDay() {

      const date = new Date(this.selectedDate)

      date.setDate(date.getDate() + 1)

      return this.formatPersianDate(date)

    },


    groupedMatches() {

      const groups = {}

      this.matches.forEach(match => {

        const competition = match.competition

        if (!competition) return

        const id = competition.id

        if (!groups[id]) {

          groups[id] = {

            id,

            name: competition.name,

            shortName: competition.shortName,

            slug: competition.slug,

            logo: competition.logo,

            matches: []

          }

        }

        groups[id].matches.push(match)

      })


      // 🇮🇷 لیگ برتر ایران اول لیست، بعد لیگ‌های معروف اروپا
      const IRAN_SLUG = 'iran-pro-league'

      const priorityOrder = [
        'PL',
        'PD',
        'SA',
        'BL1',
        'PPL',
        'DED',
        'BSA'
      ]

      const rank = (group) => {
        if (group.slug === IRAN_SLUG) return 0
        const idx = priorityOrder.indexOf(group.shortName)
        return idx === -1 ? priorityOrder.length + 1 : idx + 1
      }

      const competitions =
          Object.values(groups)


      return competitions.sort((a, b) => {

        const aIndex = rank(a)

        const bIndex = rank(b)

        // هر دو اولویت‌دار (یا هیچ‌کدام) → ترتیب الفبایی فارسی
        if (aIndex === bIndex) {

          return a.name.localeCompare(
              b.name,
              'fa'
          )

        }

        return aIndex - bIndex

      })

    }

  },


  mounted() {

    this.loadMatches()

  },


  methods: {

    // ============================
    // دریافت بازی‌ها
    // ============================

    async loadMatches() {

      this.loading = true

      this.error = null

      try {

        const date = this.formatApiDate(this.selectedDate)

        const result = await api.getMatches({
          date
        })

        /*
         * اگر بک‌اند مستقیماً آرایه برگرداند
         */
        if (Array.isArray(result)) {

          this.matches = result

        }

        /*
         * اگر بعداً API را به شکل
         * { matches: [] }
         * تغییر دادیم
         */
        else {

          this.matches = result?.matches || []

        }

      } catch (error) {

        console.error(
            '[Matches] load error:',
            error
        )

        this.error =
            'دریافت بازی‌ها با مشکل مواجه شد.'

      } finally {

        this.loading = false

      }

    },


    // ============================
    // تغییر روز
    // ============================

    changeDay(direction) {

      const date = new Date(this.selectedDate)

      date.setDate(
          date.getDate() + direction
      )

      this.selectedDate = date

      this.loadMatches()

    },


    // ============================
    // امروز
    // ============================

    goToday() {

      this.selectedDate = new Date()

      this.loadMatches()

    },


    // ============================
    // فرمت تاریخ برای API
    // ============================

    formatApiDate(date) {

      const year =
          new Intl.DateTimeFormat(
              'en-US',
              {
                year: 'numeric',
                timeZone: 'Asia/Tehran',
                calendar: 'gregory'
              }
          ).format(date)

      const month =
          new Intl.DateTimeFormat(
              'en-US',
              {
                month: '2-digit',
                timeZone: 'Asia/Tehran',
                calendar: 'gregory'
              }
          ).format(date)

      const day =
          new Intl.DateTimeFormat(
              'en-US',
              {
                day: '2-digit',
                timeZone: 'Asia/Tehran',
                calendar: 'gregory'
              }
          ).format(date)

      return `${year}-${month}-${day}`

    },


    // ============================
    // تاریخ نمایشی شمسی
    // ============================

    formatPersianDate(date) {

      const formatter =
          new Intl.DateTimeFormat(
              'fa-IR-u-ca-persian',
              {
                timeZone: 'Asia/Tehran',
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              }
          )

      const parts =
          formatter.formatToParts(date)

      const weekday =
          parts.find(
              p => p.type === 'weekday'
          )?.value || ''

      const day =
          parts.find(
              p => p.type === 'day'
          )?.value || ''

      const month =
          parts.find(
              p => p.type === 'month'
          )?.value || ''

      return {

        weekday,

        date: `${day} ${month}`

      }

    },


    // ============================
    // ساعت بازی
    // ============================

    formatTime(dateString) {

      if (!dateString) return '--:--'

      return new Intl.DateTimeFormat(
          'fa-IR',
          {
            timeZone: 'Asia/Tehran',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }
      ).format(
          new Date(dateString)
      )

    },


    // ============================
    // وضعیت Live
    // ============================

    isLive(status) {

      return [
        'LIVE',
        'IN_PLAY',
        'PAUSED'
      ].includes(status)

    },


    // ============================
    // صفحه جزئیات بازی
    // ============================

    openMatch(id) {

      this.$router.push(
          `/match/${id}`
      )

    },

    getTeamName(englishName) {
      return teamNames[englishName] || englishName;
    },
    getLeagueName(englishName) {
      return leagueNamesLatin[englishName] || englishName;
    }
  }

}

</script>


<style scoped lang="scss">

.matches-page {
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding: 32px 24px 60px;
  color: #101828;
  box-sizing: border-box;
}


/* =========================
   HEADER
========================= */

.page-header {

  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 20px;

  margin-bottom: 28px;

}

.header-info {

  display: flex;

  align-items: center;

  gap: 18px;

}

.title-icon {

  width: 58px;

  height: 58px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 16px;

  background: #351b88;

  color: white;

  font-size: 25px;

  box-shadow: 0 10px 25px rgba(53, 27, 136, .18);

}

.breadcrumbs {

  color: #98a2b3;

  font-size: 13px;

  margin-bottom: 5px;

}

.breadcrumbs span {

  margin: 0 7px;

}

.page-header h1 {

  margin: 0;

  font-size: 34px;

  font-weight: 800;

}

.page-header p {

  margin: 7px 0 0;

  color: #667085;

  font-size: 14px;

}

.league-filter {

  border: 1px solid #ded3ff;

  background: #fff;

  color: #5428c7;

  border-radius: 10px;

  padding: 11px 17px;

  font-size: 14px;

  cursor: pointer;

  transition: .2s;

}

.league-filter:hover {

  background: #f7f3ff;

}


/* =========================
   DATE NAVIGATION
========================= */

.date-navigation {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-areas: "previous today next";
  align-items: center;
}

.date-navigation > .date-side:first-child {
  grid-area: previous;
}

.date-navigation > .today-date {
  grid-area: today;
}

.date-navigation > .date-side:last-child {
  grid-area: next;
}

.date-side {

  border: 0;

  background: transparent;

  cursor: pointer;

  padding: 8px 22px;

  display: flex;

  align-items: center;

  justify-content: space-between;

  color: #172033;

}

.date-side:hover {

  background: #faf9ff;

}

.date-side div {

  display: flex;

  flex-direction: column;

  gap: 6px;

}

.date-side strong {

  font-size: 17px;

}

.date-side span {

  color: #667085;

  font-size: 13px;

}

.date-side small {

  color: #98a2b3;

  font-size: 12px;

}

.arrow {

  font-size: 32px !important;

  color: #40209d !important;

}

.today-date {

  border: 0;

  background: linear-gradient(
          135deg,
          #022c22 0%,
          #064e3b 45%,
          #047857 85%,
          #059669 100%
  );

  color: white;

  padding: 8px;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  gap: 5px;

  cursor: pointer;

  box-shadow: 0 10px 25px rgba(53, 27, 136, .22);

}

.today-label {

  font-size: 13px;

  opacity: .8;

}

.today-date strong {

  font-size: 17px;

}

.today-full-date {

  font-size: 14px;

}

.calendar-icon {

  margin-top: 5px;

  font-size: 18px;

}


/* =========================
   TIMEZONE
========================= */

.timezone-info {

  text-align: center;

  color: #667085;

  font-size: 13px;

  margin: 18px 0 27px;

}


/* =========================
   COMPETITION
========================= */

.competitions {

  display: flex;

  flex-direction: column;

  gap: 22px;

  padding-top: 40px;
}

.competition-section {

  background: white;

  border: 1px solid #e7e9ee;

  border-radius: 14px;

  overflow: hidden;

  box-shadow: 0 5px 20px rgba(16, 24, 40, .035);

}

.competition-header {

  min-height: 40px;

  display: flex;

  align-items: center;

  justify-content: space-between;

  padding: 0 20px;

  border-bottom: 1px solid #edf0f4;

  background-color: #e6f2f7a3;


}

.competition-title {

  display: flex;

  align-items: center;

  gap: 12px;

}

.competition-logo,
.competition-placeholder {

  width: 35px;

  height: 35px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 9px;

  background: #f7f7fa;

}

.competition-logo img {

  width: 28px;

  height: 28px;

  object-fit: contain;

}

.competition-placeholder {

  font-size: 18px;

}

.competition-title h2 {

  margin: 0;

  font-size: 18px;

  font-weight: 700;

}

.match-count {

  color: #667085;

  font-size: 13px;

}


/* =========================
   MATCH ROW
========================= */

/* =========================
   MATCH ROW
========================= */

.match-row {

  min-height: 52px;

  display: grid;

  grid-template-columns: 1fr 150px 1fr;

  align-items: center;

  gap: 20px;

  padding: 8px 20px;

  border-bottom: 1px solid #f0f2f5;

  cursor: pointer;

  transition: .18s;

}

.match-row:last-child {
  border-bottom: 0;
}

.match-row:hover {
  background: #fbfaff;
}


/* =========================
   TEAMS
========================= */

.team {

  display: flex;

  align-items: center;

  gap: 10px;

  font-size: 14px;

  font-weight: 500;

  min-width: 0;

}

.clickable-team {
  cursor: pointer;

  &:hover span {
    color: #2563eb;
    text-decoration: underline;
  }
}

.home-team {
  justify-content: flex-start;
}

.away-team {
  justify-content: flex-end;
  text-align: right;
}

.team span {

  white-space: nowrap;

  overflow: hidden;

  text-overflow: ellipsis;

  font-weight: 600;

  color: gray;

}

.team img {

  width: 25px;

  height: 25px;

  object-fit: contain;

  flex-shrink: 0;

}


/* =========================
   CENTER
========================= */

.match-center {

  display: flex;

  align-items: center;

  justify-content: center;

  min-width: 0;

}

.kickoff,
.score {

  font-size: 16px;

  font-weight: 800;

  line-height: 1;

}

.finished-score {
  color: #344054;
}

.live-center {

  display: flex;

  align-items: center;

  justify-content: center;

  gap: 8px;

}

.live-score {
  color: #d92d20;
}


/* =========================
   STATUS
========================= */

.status-dot {

  display: inline-flex;

  align-items: center;

  gap: 4px;

  padding: 3px 7px;

  border-radius: 20px;

  font-size: 10px;

  font-weight: 600;

  white-space: nowrap;

}

.live-dot {

  color: #d92d20;

  background: #fff1f0;

}

.live-dot i {

  width: 5px;

  height: 5px;

  border-radius: 50%;

  background: #d92d20;

}

.finished-dot {

  color: #667085;

  background: #f2f4f7;

}

.live-score {

  color: #d92d20;

}


.status.scheduled {

  color: #175cd3;

  background: #eff6ff;

}

.status.finished {

  color: #087443;

  background: #ecfdf3;

}

.status.live {

  color: #d92d20;

  background: #fff1f0;

  gap: 5px;

}

.status.live i {

  width: 6px;

  height: 6px;

  border-radius: 50%;

  background: #d92d20;

}


/* =========================
   LOADING
========================= */

.loading {

  min-height: 300px;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  gap: 15px;

  color: #667085;

}

.loader {

  width: 35px;

  height: 35px;

  border: 3px solid #eee;

  border-top-color: #43249b;

  border-radius: 50%;

  animation: spin .8s linear infinite;

}

@keyframes spin {

  to {
    transform: rotate(360deg);
  }

}


/* =========================
   ERROR
========================= */

.error-box {

  padding: 25px;

  border-radius: 14px;

  background: #fff5f5;

  color: #b42318;

  text-align: center;

}

.error-box button {

  display: block;

  margin: 15px auto 0;

  border: 0;

  border-radius: 8px;

  padding: 8px 16px;

  cursor: pointer;

}


/* =========================
   EMPTY
========================= */

.empty-state {

  padding: 70px 20px;

  text-align: center;

  color: #667085;

}

.empty-icon {

  font-size: 45px;

  margin-bottom: 15px;

}

.empty-state h3 {

  margin-bottom: 8px;

  color: #344054;

}


/* =========================
   FOOTER CTA
========================= */

.matches-footer {

  margin-top: 25px;

  display: flex;

  align-items: center;

  gap: 15px;

  padding: 18px 22px;

  border: 1px solid #ded3ff;

  border-radius: 13px;

  background: #faf8ff;

}

.footer-icon {

  width: 45px;

  height: 45px;

  border-radius: 11px;

  display: flex;

  align-items: center;

  justify-content: center;

  background: #eee8ff;

  font-size: 21px;

}

.matches-footer strong {

  color: #2f1b72;

}

.matches-footer p {

  margin: 5px 0 0;

  font-size: 12px;

  color: #667085;

}


/* =========================
   RESPONSIVE
========================= */

@media (max-width: 768px) {

  .match-row {

    grid-template-columns: 1fr 85px 1fr;

    gap: 4px;

    min-height: 64px;

    padding: 7px 8px;

  }

  .team {

    font-size: 11px;

    gap: 5px;

  }

  .team img {

    width: 28px;

    height: 28px;

  }

  .matches-page {

    padding: 20px 12px 40px;

  }

  .page-header {

    align-items: flex-start;

  }

  .page-header h1 {

    font-size: 27px;

  }

  .league-filter {

    display: none;

  }

  .date-navigation {

    grid-template-columns: 1fr 1fr 1fr;

  }

  .today-date {
    order: 2;

  }

  .date-side {

    padding: 13px 8px;

  }

  .date-side strong {

    font-size: 13px;

  }

  .date-side span,
  .date-side small {

    font-size: 10px;

  }

  .arrow {

    font-size: 25px !important;

  }

  .today-date {

    padding: 12px 5px;

  }

  .today-label,
  .today-full-date {

    font-size: 10px;

  }

  .today-date strong {

    font-size: 13px;

  }

  .kickoff,
  .score {

    font-size: 13px;

  }

  .live-center {

    gap: 4px;

  }

  .status-dot {

    font-size: 8px;

    padding: 2px 5px;

  }

  .live-dot i {

    width: 4px;

    height: 4px;

  }

  .status {

    font-size: 9px;

    padding: 3px 6px;

  }

  .competition-header {
    min-height: 40px;

    display: flex;

    align-items: center;

    justify-content: space-between;

    padding: 0 28px;

    border-bottom: 1px solid #edf0f4;

    background-color: #e6f2f7a3;
  }

  .competition-title {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .competition-logo,
  .competition-placeholder {
    width: 40px;
    height: 40px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 10px;

    background: #f7f7fa;
  }

  .competition-logo img {
    width: 32px;
    height: 32px;
    object-fit: contain;
  }

  .competition-title h2 {
    margin: 0;

    font-size: 19px;

    font-weight: 750;
  }

  .match-count {

    font-size: 11px;

  }

  .matches-footer {

    padding: 14px;

  }

}

</style>